// :)
/**
 * Client-side pack verification — the trust gate for `synkro sync`.
 *
 * Mirrors packages/api/src/lib/packSigning.ts BYTE-FOR-BYTE: the canonical form
 * (sorted-key JSON over {rules, docs, manifest}) must reproduce exactly the
 * bytes the publisher hashed + signed, or the digest check fails. Nothing binds
 * unless verifyPack() returns true — recompute the digest from what we fetched,
 * confirm it matches the claimed digest, then verify the Ed25519 signature
 * against the distributed public key. Fail closed.
 */

import crypto from 'node:crypto';

export interface PackContent {
  rules: unknown[];
  docs?: Array<{ path: string; content: string }>;
  manifest?: Record<string, unknown>;
}

/** Deterministic JSON: object keys sorted recursively, arrays order-preserved. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map(stableStringify).join(',') + ']';
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',') + '}';
}

// CONTENT address — rules + docs only, NOT the manifest. Must stay byte-identical
// to the API's packSigning.canonicalize so a client-recomputed digest matches.
export function canonicalize(pack: PackContent): string {
  return stableStringify({
    rules: pack.rules ?? [],
    docs: pack.docs ?? [],
  });
}

export function computeDigest(canonical: string): string {
  return 'sha256:' + crypto.createHash('sha256').update(canonical, 'utf8').digest('hex');
}

export function verifySignature(digest: string, signatureB64: string, publicKeyPem: string): boolean {
  try {
    const key = crypto.createPublicKey(publicKeyPem);
    return crypto.verify(null, Buffer.from(digest, 'utf8'), key, Buffer.from(signatureB64, 'base64'));
  } catch {
    return false;
  }
}

/**
 * Recompute the digest from the pack we fetched, confirm it equals the claimed
 * digest (catches a tampered blob/row), then verify the signature. Fail closed.
 */
export function verifyPack(
  pack: PackContent,
  claimedDigest: string,
  signatureB64: string,
  publicKeyPem: string,
): boolean {
  const recomputed = computeDigest(canonicalize(pack));
  if (recomputed !== claimedDigest) return false;
  return verifySignature(claimedDigest, signatureB64, publicKeyPem);
}
